import React, { useMemo } from 'react';
import { Badge, Button, Input, Segmented, Table } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import PendingOnboardingManagement from '../PoliciesManagement';
import StaffUploadsPanel from './StaffUploadsPanel';
import { getPendingOnboardingCount } from '../orgDetailTabHelpers';
import { resolvePlainOrgCode } from '../enrollmentConflictHelpers';

const { Search } = Input;

export default function OrgMembersPanel({
  section,
  onSectionChange,
  tabCounts,
  pendingStaffUploadsCount = 0,
  showUploadsSection = false,
  canReviewStaffUploads = false,
  canSeeUploadHistory = false,
  orgRecord,
  socialNo,
  onPendingCountChange,
  policiesColumns,
  policiesData,
  policiesSearch,
  setPoliciesSearch,
  policiesLoading,
  refetchPolicies,
  pagination,
  goToPage,
}) {
  const pendingOnboardingCount = getPendingOnboardingCount(tabCounts);

  const sectionOptions = useMemo(() => {
    const options = [
      {
        label: `Active (${tabCounts.active?.count || 0})`,
        value: 'active',
      },
      {
        label: `Inactive (${tabCounts.inactive?.count || 0})`,
        value: 'inactive',
      },
      {
        label: `Onboarding (${pendingOnboardingCount})`,
        value: 'onboarding',
      },
    ];

    if (showUploadsSection) {
      options.push({
        label: (
          <span className={`dash-tab-label${canReviewStaffUploads && pendingStaffUploadsCount > 0 ? ' dash-tab-label--attention' : ''}`}>
            <span>Uploads</span>
            {canReviewStaffUploads && pendingStaffUploadsCount > 0 ? (
              <Badge
                count={pendingStaffUploadsCount}
                size="small"
                overflowCount={99}
                className="dash-tab-badge"
              />
            ) : null}
          </span>
        ),
        value: 'uploads',
      });
    }

    return options;
  }, [
    tabCounts.active?.count,
    tabCounts.inactive?.count,
    pendingOnboardingCount,
    showUploadsSection,
    canReviewStaffUploads,
    pendingStaffUploadsCount,
  ]);

  const renderPoliciesTable = (placeholder) => (
    <>
      <div className="org-detail-toolbar">
        <Search
          className="org-detail-toolbar__search"
          size="small"
          placeholder={placeholder}
          value={policiesSearch}
          onChange={(e) => setPoliciesSearch(e.target.value)}
          onSearch={(value) => setPoliciesSearch(value)}
          allowClear
        />
        <Button
          size="small"
          icon={<SyncOutlined spin={policiesLoading} />}
          onClick={() => refetchPolicies()}
          loading={policiesLoading}
        >
          Refresh
        </Button>
      </div>
      <Table
        className="org-policies-table"
        columns={policiesColumns}
        dataSource={policiesData.map((val, index) => ({ ...val, key: String(index) }))}
        loading={policiesLoading}
        size="small"
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          onChange: goToPage,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </>
  );

  return (
    <div className="org-members-panel">
      <div className="dash-tab-strip dash-tab-strip--compact org-members-panel__strip" role="tablist" aria-label="Members views">
        <Segmented
          value={section}
          onChange={onSectionChange}
          options={sectionOptions}
        />
      </div>
      <p className="org-members-panel__hint">Roster, onboarding pipeline, and HR upload review for this organization.</p>

      {section === 'active' ? renderPoliciesTable('Search active policies…') : null}
      {section === 'inactive' ? renderPoliciesTable('Search inactive policies…') : null}
      {section === 'onboarding' ? (
        <PendingOnboardingManagement
          socialNo={socialNo}
          organizationData={orgRecord}
          tabCounts={tabCounts}
        />
      ) : null}
      {section === 'uploads' && showUploadsSection ? (
        <StaffUploadsPanel
          showOrgFilter={false}
          filterOrgId={orgRecord?.org_id}
          filterOrgLabel={orgRecord?.org_name || ''}
          filterSocialNo={resolvePlainOrgCode(orgRecord?.org_social_no, socialNo)}
          onPendingCountChange={onPendingCountChange}
          showApprovalQueue={canReviewStaffUploads}
          showUploadHistory={canSeeUploadHistory}
        />
      ) : null}
    </div>
  );
}
